import moment from 'moment';
import React from 'react';
import { ReactComponent as OrderPendingIcon } from '@ubeya/shared-web/assets/exclamation-mark-outlined.svg';
import { ReactComponent as OrderCancelledIcon } from '@ubeya/shared-web/assets/delete.svg';

import { ReactComponent as BaseOrderCompletedIcon } from '@ubeya/shared-web/assets/confirmation.svg';
import { ReactComponent as BaseChatIconBlue } from '@ubeya/shared-web/assets/chat-blue.svg';
import { ReactComponent as BaseShiftCaptainIcon } from '@ubeya/shared-web/assets/captain-shift-active.svg';
import { ReactComponent as BaseAreaCaptainIcon } from '@ubeya/shared-web/assets/captain-area-active.svg';
import { ReactComponent as BaseEventCaptainIcon } from '@ubeya/shared-web/assets/captain-event-active.svg';
import { ReactComponent as BaseTasksCaptainIcon } from '@ubeya/shared-web/assets/captain-task-active.svg';
import { ReactComponent as ChatIcon } from '@ubeya/shared-web/assets/chat.svg';

import styled, { css } from 'styled-components';
import { LEGENDS_US_BARCLAYS_ACCOUNT_ID } from './utils/accounts';

export const EMPLOYEES_TABS = {
  active: { statusFilter: 'active', dataKey: 'active', tooltip: 'employeesTabActive' },
  inactive: { statusFilter: 'inactive', dataKey: 'inactive', tooltip: 'employeesTabDeactivated' },
  deleted: { statusFilter: 'deleted', dataKey: 'deleted', tooltip: 'employeesTabDeleted' },
  pending: { statusFilter: 'isPending', dataKey: 'isPending', hideCaseEmpty: true },
  candidates: { statusFilter: 'candidates', dataKey: 'candidates', tooltip: 'employeesTabCandidates' }
};

const iconCSS = css`
  ${({ preventHoverStyle }) =>
    !preventHoverStyle &&
    css`
      &:hover {
        > circle {
          fill: ${({ theme }) => theme.colors.primary};
        }
      }
    `}
`;

const OrderCompletedIcon = styled(BaseOrderCompletedIcon)`
  path {
    fill: ${({ theme }) => theme.colors.greenPositive};
  }
`;

const ShiftCaptainIcon = styled(BaseShiftCaptainIcon)`
  ${iconCSS};
`;

const AreaCaptainIcon = styled(BaseAreaCaptainIcon)`
  ${iconCSS};
`;

const EventCaptainIcon = styled(BaseEventCaptainIcon)`
  ${iconCSS}
`;

const TasksCaptainIcon = styled(BaseTasksCaptainIcon)`
  ${iconCSS};
`;

const OrderChangesPendingIcon = styled(OrderPendingIcon)`
  path {
    fill: ${({ theme }) => theme.colors.orangeChanges};
  }
`;

export const COMMENT_TYPES = [
  {
    id: 1,
    slug: 'comment__other'
  },
  {
    id: 2,
    slug: 'comment__disciplinary'
  },
  {
    id: 3,
    slug: 'comment__late'
  },
  {
    id: 4,
    slug: 'comment__employment'
  },
  {
    id: 5,
    slug: 'comment__eca',
    restrictToAccountIds: [LEGENDS_US_BARCLAYS_ACCOUNT_ID]
  }
];

export const SUPPLIERS_TABS = {
  suppliers: { key: 'suppliers' }
};

export const SETTINGS__ACCOUNTS_TABS = [
  'info',
  'branches',
  'fields',
  'positions',
  'tags',
  'modules',
  'payroll',
  'departments'
];

export const CLOCK_METHODS = {
  SELF: { slug: 'clockMethodSelf', value: 0 },
  EMPLOYEE_QR_SCAN: { slug: 'clockMethodEmployeeQRScan', value: 1 },
  MANAGER_QR_SCAN: { slug: 'clockMethodManagerQRScan', value: 2 }
};

export const GRADUAL_PUBLISH_STATUS = {
  NONE: 0,
  ACTIVE: 1,
  FINISHED: 2
};

export const ORDER_STATUSES = {
  ORDER_INITIATED: {
    label: 'order_initiated',
    color: ({ theme }) => theme.colors.white,
    colorLight: ({ theme }) => theme.colors.black,
    background: ({ theme }) => theme.colors.grey2,
    backgroundLight: ({ theme }) => theme.colors.grey2,
    slug: 'order_initiated',
    icon: OrderPendingIcon,
    nextStatus: 'client_order_approved',
    hideInSchedulingContext: true
  },
  DRAFT: {
    label: 'order_draft',
    color: ({ theme }) => theme.colors.white,
    colorLight: ({ theme }) => theme.colors.black,
    background: ({ theme }) => theme.colors.grey2,
    backgroundLight: ({ theme }) => theme.colors.grey2,
    slug: 'order_draft',
    icon: OrderPendingIcon,
    nextStatus: 'supplier_order_pending_approval'
  },
  CLIENT_ORDER_PENDING_APPROVAL: {
    label: 'client_order_pending_approval',
    color: ({ theme }) => theme.colors.white,
    colorLight: ({ theme }) => theme.colors.black,
    background: ({ theme }) => theme.colors.grey2,
    backgroundLight: ({ theme }) => theme.colors.grey2,
    slug: 'client_order_pending_approval',
    icon: OrderPendingIcon,
    nextStatus: 'supplier_order_approved'
  },
  SUPPLIER_ORDER_PENDING_APPROVAL: {
    label: 'supplier_order_pending_approval',
    color: ({ theme }) => theme.colors.grey4,
    colorLight: ({ theme }) => theme.colors.black,
    background: ({ theme }) => theme.colors.grey2,
    backgroundLight: ({ theme }) => theme.colors.grey2,
    slug: 'supplier_order_pending_approval',
    icon: OrderPendingIcon,
    nextStatus: 'supplier_order_approved'
  },
  SUPPLIER_ORDER_APPROVED: {
    label: 'supplier_order_approved',
    color: ({ theme }) => theme.colors.black,
    colorLight: ({ theme }) => theme.colors.black,
    background: ({ theme }) => theme.colors.greenPositive,
    backgroundLight: ({ theme }) => `${theme.colors.greenPositive}33`,
    slug: 'supplier_order_approved',
    icon: OrderCompletedIcon,
    nextStatus: null,
    changeButtonLabel: 'supplier_order_approved_change_button'
  },
  SUPPLIER_BOOKING_COMPLETED: {
    label: 'supplier_booking_completed',
    color: ({ theme }) => theme.colors.black,
    colorLight: ({ theme }) => theme.colors.black,
    background: ({ theme }) => theme.colors.greenPositive,
    backgroundLight: ({ theme }) => theme.colors.greenLight,
    slug: 'supplier_booking_completed',
    icon: OrderCompletedIcon,
    nextStatus: null,
    changeButtonLabel: 'supplier_booking_completed_change_button'
  },
  CLIENT_APPROVED_HOURS: {
    label: 'client_approved_hours',
    color: ({ theme }) => theme.colors.black,
    colorLight: ({ theme }) => theme.colors.black,
    background: ({ theme }) => theme.colors.greenPositive,
    backgroundLight: ({ theme }) => theme.colors.greenLight,
    slug: 'client_approved_hours',
    icon: OrderCompletedIcon,
    nextStatus: null,
    changeButtonLabel: 'client_approved_hours_change_button'
  },
  SUPPLIER_ORDER_COMPLETED: {
    label: 'supplier_order_completed',
    color: ({ theme }) => theme.colors.white,
    colorLight: ({ theme }) => theme.colors.black,
    background: ({ theme }) => theme.colors.greenPositive,
    backgroundLight: ({ theme }) => theme.colors.greenLight,
    slug: 'supplier_order_completed',
    icon: OrderCompletedIcon,
    nextStatus: null,
    changeButtonLabel: 'supplier_order_completed_change_button'
  },
  CHANGES_REQUESTED: {
    label: 'changes_requested',
    color: ({ theme }) => theme.colors.white,
    colorLight: ({ theme }) => theme.colors.black,
    background: ({ theme }) => theme.colors.orangeChanges,
    backgroundLight: ({ theme }) => theme.colors.orangeLight,
    slug: 'changes_requested',
    icon: OrderChangesPendingIcon,
    nextStatus: 'supplier_order_approved'
  },
  CANCELLATION_REQUESTED: {
    label: 'cancellation_requested',
    color: ({ theme }) => theme.colors.white,
    colorLight: ({ theme }) => theme.colors.black,
    background: ({ theme }) => theme.colors.orangeChanges,
    backgroundLight: ({ theme }) => theme.colors.orangeLight,
    slug: 'cancellation_requested',
    icon: OrderChangesPendingIcon,
    nextStatus: 'supplier_order_approved'
  },
  CANCELED: {
    label: 'order_canceled',
    color: ({ theme }) => theme.colors.white,
    colorLight: ({ theme }) => theme.colors.grey4,
    background: ({ theme }) => theme.colors.grey3,
    backgroundLight: ({ theme }) => theme.colors.grey2,
    slug: 'order_canceled',
    icon: OrderCancelledIcon,
    changeButtonLabel: 'order_canceled_change_button'
  }
};

export const ORDER_STATUSES_OPTIONS = Object.values(ORDER_STATUSES).map(({ label }) => label);

/**
 * Constants for SequencesTab context values
 */
export const SEQUENCES_CONTEXT = {
  SEQUENCES: 'sequences',
  TASKS: 'tasks'
};

export const ORDER_CHANGES = {
  DECISIONS: {
    ACCEPTED: 'accepted',
    REJECTED: 'rejected'
  },
  REQUEST_TYPES: {
    NEW: 'new',
    CHANGE: 'change',
    CANCEL: 'cancel',
    ORDER_PENDING: 'order-pending'
  }
};

export const ACCOUNT_BILLING_STATUS_MAP = {
  STAFFING_AGENCY_FREEMIUM: 'staffingAgencyFreemium',
  CLIENT_FREEMIUM: 'clientFreemium',
  INTERNAL: 'internal',
  WARNING: 'warning',
  TRIAL: 'trial',
  PAYING: 'paying',
  BLOCKED: 'blocked',
  FROZEN: 'frozen',
  UNQUALIFIED: 'unqualified',
  ONBOARDING: 'onboarding',
  NETWORK_ACCESS_UNLIMITED_ADMINS: 'networkAccessUnlimitedAdmins',
  PILOT: 'pilot'
};

export const ACCOUNT_STATUSES = [
  { label: 'Trial', value: ACCOUNT_BILLING_STATUS_MAP.TRIAL },
  { label: 'Paying', value: ACCOUNT_BILLING_STATUS_MAP.PAYING },
  { label: 'Demo', value: ACCOUNT_BILLING_STATUS_MAP.DEMO },
  { label: 'Warning', value: ACCOUNT_BILLING_STATUS_MAP.WARNING },
  { label: 'Blocked', value: ACCOUNT_BILLING_STATUS_MAP.BLOCKED },
  { label: 'Frozen', value: ACCOUNT_BILLING_STATUS_MAP.FROZEN },
  { label: 'Unqualified', value: ACCOUNT_BILLING_STATUS_MAP.UNQUALIFIED },
  { label: 'Onboarding', value: ACCOUNT_BILLING_STATUS_MAP.ONBOARDING },
  { label: 'Pilot', value: ACCOUNT_BILLING_STATUS_MAP.PILOT },
  { label: 'Staffing Agency Freemium', value: ACCOUNT_BILLING_STATUS_MAP.STAFFING_AGENCY_FREEMIUM },
  { label: 'Client Freemium', value: ACCOUNT_BILLING_STATUS_MAP.CLIENT_FREEMIUM },
  { label: 'Internal', value: ACCOUNT_BILLING_STATUS_MAP.INTERNAL },
  { label: 'Network Access - Unlimited Admins', value: ACCOUNT_BILLING_STATUS_MAP.NETWORK_ACCESS_UNLIMITED_ADMINS }
];

export const SETTINGS__BRANCH_TABS = ['info', 'timesheet', 'schedule', 'employees', 'locations', 'uniforms', 'payroll'];

export const SETTINGS__BRANCH_HOST_TABS = ['info'];

export const TIME_FORMAT = {
  12: 'hh:mm a',
  24: 'HH:mm'
};

export const DATE_FORMAT = ['DD/MM/YYYY', 'MM/DD/YYYY'];

export const DAYS_OF_WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

export const getDatesRange = () => ({
  yesterday: { start: moment().subtract(1, 'day').startOf('day'), end: moment().subtract(1, 'day').endOf('day') },
  today: { start: moment().startOf('day'), end: moment().endOf('day') },
  thisWeek: { start: moment().startOf('week').startOf('day'), end: moment().endOf('week').endOf('day') },
  lastWeek: {
    start: moment().subtract(1, 'week').startOf('week').startOf('day'),
    end: moment().subtract(1, 'week').endOf('week').endOf('day')
  },
  nextWeek: {
    start: moment().add(1, 'week').startOf('week').startOf('day'),
    end: moment().add(1, 'week').endOf('week').endOf('day'),
    isFuture: true
  },
  thisMonth: {
    start: moment().startOf('month').startOf('day'),
    end: moment().endOf('month').endOf('day')
  },
  lastMonth: {
    start: moment().subtract(1, 'month').startOf('month').startOf('day'),
    end: moment().subtract(1, 'month').endOf('month').endOf('day')
  },
  nextMonth: {
    start: moment().add(1, 'month').startOf('month').startOf('day'),
    end: moment().add(1, 'month').endOf('month').endOf('day'),
    isFuture: true
  },
  custom: { start: moment().startOf('day'), end: moment().endOf('day') }
});

export const ALLOWED_PHOTO_TYPES = '.jpeg,.jpg,.gif,.png';

export const ALLOWED_VIDEOS_TYPES = '.mp4,.mov,.qt';

export const ALLOWED_DOCUMENT_EXTENSIONS = '.jpeg,.jpg,.gif,.png,.pdf,.doc,.docx,.ppt,.pptx,.xlsx';

export const ALLOWED_DOCUMENT_FILE_EXTENSIONS = '.pdf,.doc,.docx,.ppt,.pptx,.xlsx';

export const LOCATION_ATTACHMENTS_EXTENSIONS = '.mp4,.mov,.qt,.jpeg,.jpg,.gif,.png,.pdf';

export const ALLOWED_FILE_EXTENSIONS = '.mp4,.mov,.qt,.jpeg,.jpg,.gif,.png,.pdf,.doc,.docx,.ppt,.pptx,.xlsx';

export const ATTACHMENTS_TYPES = {
  ...ALLOWED_DOCUMENT_EXTENSIONS.split(',').reduce((all, curr) => ({ ...all, [curr.slice(1)]: 'document' }), {}),
  ...ALLOWED_PHOTO_TYPES.split(',').reduce((all, curr) => ({ ...all, [curr.slice(1)]: 'image' }), {}),
  ...ALLOWED_VIDEOS_TYPES.split(',').reduce((all, curr) => ({ ...all, [curr.slice(1)]: 'video' }), {})
};

export const IMAGE_ATTACHMENTS_TYPES = ALLOWED_PHOTO_TYPES.split(',').map((extension) => extension.slice(1));

export const API_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const API_DATE_TIME_FORMAT_ZERO_SECONDS = 'YYYY-MM-DD HH:mm:00';
export const API_TIME_FORMAT = 'HH:mm:ss';
export const API_TIME_FORMAT_NO_SECONDS = 'HH:mm';
export const API_DATE_FORMAT = 'YYYY-MM-DD';

export const POLLING_INTERVAL = 7 * 1000;
export const LONG_POLLING_INTERVAL = 1 * 60 * 1000;

//Chat - Post message
export const PM_EMPLOYEE_CLICKED = 'employee-clicked';
export const PM_CHAT_COUNTER = 'chat-counter';
export const PM_CLOSE_CHAT_MOBILE = 'close-chat-mobile';
export const PM_CHAT_ARCHIVE_AMPLITUDE_TRACK = 'chat-archive-amplitude-track';

//shift duration
export const MAX_HOURS_SHOW_PROMPT = 17;
export const MAX_HOURS_WARNING = 35;

// Weekly refresh interval
export const REFRESH_WEEKLY_INTERVAL = 1000 * 20;

// Timesheet slot fields
export const SLOT_FIELDS = {
  payrollNotes: 'payrollNotes',
  rating: 'rating',
  reviewOnEmployee: 'review'
};

//Project employee slot fields
export const EMPLOYEE_SLOT_FIELDS = {
  payrollNotes: 'payrollNotes',
  rating: 'rating',
  reviewOnEmployee: 'review'
};

export const PAYROLL_FIELD_TIP_ID = 1;
export const PAYROLL_FIELD_TIP_BONUS_ID = 85;
export const PAYROLL_FIELD_TIP_ID_7036 = 94;
export const PAYROLL_FIELD_TRAVEL_ID = 2;
export const PAYROLL_FIELD_TRAVEL_PARKING_ID = 137;
export const ALWAYS_EDITABLE_ON_TIMESHEET_PAYROLL_FIELD_IDS = [PAYROLL_FIELD_TIP_ID, PAYROLL_FIELD_TRAVEL_ID];

export const PAYROLL_PERIOD_TYPES = {
  MONTH: 'month',
  WEEK: 'week'
};

export const REQUEST_STATUS = {
  PENDING: 0,
  APPROVED: 1,
  DECLINED: 2
};

export const REQUESTS_STATUS = {
  PENDING: 'pending',
  HISTORY: 'history'
};

export const CREATE_USER_BY = {
  PHONE: 0,
  EMAIL: 1
};

export const FILTERS_IDS = {
  POSITIONS_FILTER_ID: 1,
  AGE: 3,
  RESTRICTED_CLIENTS_FILTER_ID: 12,
  NOT_BOOKED_ON_DATE_FILTER_ID: 18,
  PREFERRED_CLIENTS_FILTER_ID: 19,
  START_EMPLOYMENT_DATE: 20,
  AVAILABLE_BY_TIME_AVAILABILITY: 23,
  DEPARTMENT: 25,
  WORKED_IN_AREA_BEFORE: 27,
  PREFERRED_AREA: 35,
  NOT_EXCEEDING_EARNINGS_LIMIT: 36,
  CONTRACT: 30,
  MAIN_PROJECTS_FILTER_ID: 31
};

export const EMPLOYEE_JOB_MONTHS_AGO = 240;
export const EMPLOYEE_JOB_MONTHS_AHEAD = 18;

export const EMPLOYEE_JOB_STATS = {
  pastFromDate: moment().subtract(EMPLOYEE_JOB_MONTHS_AGO, 'months').utc().format(API_DATE_FORMAT),
  pastToDate: moment().format(API_DATE_FORMAT),
  futureFromDate: moment().format(API_DATE_FORMAT),
  futureToDate: moment().add(EMPLOYEE_JOB_MONTHS_AHEAD, 'months').format(API_DATE_FORMAT),
  includeRejectedApplications: true
};

export const FEATURE_GATE_TYPES = {
  EVENTS: 'events',
  AVAILABILITY_CHECK: 'availability'
};

// warnings and infos
export const WARNINGS_AND_INFOS_TYPES = {
  TOTAL_WEEK_HOURS: 1,
  TOTAL_MONTH_HOURS: 2
};

export const TIMESHEET_GROUP_BY_OPTIONS = [
  { label: 'groupByEmployee', value: 'employee' },
  { label: 'groupByDate', value: 'date' },
  { label: 'groupByPosition', value: 'position' },
  { label: 'groupByEvent', value: 'project' },
  { label: 'groupByMainEvent', value: 'mainEvent' },
  { label: 'groupByClient', value: 'client' },
  { label: 'groupByCostCenter', value: 'costCenter' }
];

export const INVOICES_RECEIVED_GROUP_BY_OPTIONS = [
  { label: 'groupByEmployee', value: 'employee' },
  { label: 'groupByDate', value: 'date' },
  { label: 'groupByPosition', value: 'position' },
  { label: 'groupByEvent', value: 'project' },
  { label: 'groupByPONumber', value: 'poNumber' }
];

export const INVOICES_CREATE_GROUP_BY_OPTIONS = [
  { label: 'groupByEmployee', value: 'employee' },
  { label: 'groupByDate', value: 'date' },
  { label: 'groupByPosition', value: 'position' },
  { label: 'groupByEvent', value: 'project' },
  { label: 'groupByPONumber', value: 'poNumber' },
  { label: 'noGroupBy', value: 'noGroupBy' }
];

export const TIMESHEET_GROUP_BY_OPTIONS_ENUM = {
  EMPLOYEE: 'employee',
  DATE: 'date',
  POSITION: 'position',
  PROJECT: 'project',
  MAIN_EVENT: 'mainEvent',
  START_TIME: 'startTime',
  END_TIME: 'endTime',
  LOCATION: 'location',
  CLIENT: 'client',
  COST_CENTER: 'costCenter',
  PO_NUMBER: 'poNumber',
  NO_GROUP_BY: 'noGroupBy'
};

export const TIMESHEET_DATA_KEY_OPTIONS = {
  START: 'start',
  END: 'end',
  CHECK_IN: 'checkIn',
  CHECK_OUT: 'checkOut'
};

export const FIGARO_LATEST_VERSION = 7;

export const CAPTAIN_TYPES = {
  EVENT: 1,
  SHIFT: 2,
  AREA: 3,
  TASKS: 4
};

export const CAPTAIN_TYPES_SLUGS = {
  EVENT: 'eventCaptain',
  SHIFT: 'shiftCaptain',
  AREA: 'areaCaptain',
  TASKS: 'tasksCaptain'
};

export const CAPTAIN_ICONS_BY_TYPE = {
  [CAPTAIN_TYPES.SHIFT]: ShiftCaptainIcon,
  [CAPTAIN_TYPES.EVENT]: EventCaptainIcon,
  [CAPTAIN_TYPES.AREA]: AreaCaptainIcon,
  [CAPTAIN_TYPES.TASKS]: TasksCaptainIcon
};

export const TAG_COLORS = [
  { id: 1, color: '#1abc9c' },
  { id: 2, color: '#2ecc71' },
  { id: 3, color: '#3498db' },
  { id: 4, color: '#9b59b6' },
  { id: 5, color: '#34495e' },
  { id: 6, color: '#f1c40f' },
  { id: 7, color: '#e67e22' },
  { id: 8, color: '#e74c3c' },
  { id: 9, color: '#bdc3c7' },
  { id: 10, color: '#9c88ff' },
  { id: 11, color: '#273c75' },
  { id: 12, color: '#192a56' },
  { id: 13, color: '#F43CCC' },
  { id: 14, color: '#FBA0BB' },
  { id: 15, color: '#66D1FF' },
  { id: 16, color: '#56E2D1' },
  { id: 17, color: '#9F7645' },
  { id: 18, color: '#D98FFB' },
  { id: 19, color: '#FF9881' }
];

const chatIconStyle = css`
  width: 24px;
  height: 24px;
`;

const ChatIconBlue = styled(BaseChatIconBlue)`
  ${chatIconStyle}
`;

const ChatIconGray = styled(ChatIcon)`
  ${chatIconStyle}
`;

export const CHAT_ICONS = {
  BLUE: {
    type: 'BLUE',
    component: <ChatIconBlue />
  },
  GRAY: {
    type: 'GRAY',
    component: <ChatIconGray />
  }
};

export const MAIN_PROJECT_NO_COLOR = '#cce2ff66';
export const MAIN_PROJECT_NO_COLOR_HOVER = '#B8D6FF';

export const MAIN_PROJECT_COLORS = [
  { id: 0, color: MAIN_PROJECT_NO_COLOR },
  { id: 1, color: '#006AFF' },
  { id: 2, color: '#9E30E1' },
  { id: 3, color: '#4447B9' },
  { id: 4, color: '#14AFB4' },
  { id: 5, color: '#FF8833' },
  { id: 6, color: '#FF3F97' },
  { id: 7, color: '#000000' }
];

export const LEGAL_AGE = 18;

export const QUERY_CACHE_TIME = 15 * 60 * 1000; // 15 minutes

export const getSelectedBranchKeyByAccountId = (accountId) => `selectedBranchId-${accountId}`;

export const ACCOUNTS_CONTRACTS_TYPES = {
  Casual: 'Casual',
  MGH: 'MGH',
  Permanent: 'Permanent',
  Casual_Accruing: 'Casual_Accruing',
  MINI_DE: 'Mini_DE',
  MIDI_DE: 'Midi_DE',
  MIDI_MONTHLY_DE: 'Midi_Monthly_DE',
  PERM_DE: 'Perm_DE',
  BLUE_COLLAR_SWEDEN: 'Blue_Collar_Sweden',
  BLUE_COLLAR_PERM_SWEDEN: 'Blue_Collar_Perm_Sweden',
  WHITE_COLLAR_SWEDEN: 'White_Collar_Sweden',
  NON_PAID_SWEDEN: 'Non_Paid_Sweden'
};

export const ContractTypesWithMinimums = [
  ACCOUNTS_CONTRACTS_TYPES.MGH,
  ACCOUNTS_CONTRACTS_TYPES.Casual_Accruing,
  ACCOUNTS_CONTRACTS_TYPES.MIDI_DE,
  ACCOUNTS_CONTRACTS_TYPES.MINI_DE,
  ACCOUNTS_CONTRACTS_TYPES.MIDI_MONTHLY_DE
];

export const ContractTypesWithRateOfPay = [ACCOUNTS_CONTRACTS_TYPES.MGH];

export const ContractTypesWithMaximumEarnings = [
  ACCOUNTS_CONTRACTS_TYPES.MINI_DE,
  ACCOUNTS_CONTRACTS_TYPES.MIDI_DE,
  ACCOUNTS_CONTRACTS_TYPES.MIDI_MONTHLY_DE
];

// Contracts that accrue holiday pay as a running money balance ("pot") on the employee contract.
// Used to gate money-based UI annotations (e.g. balance amount alongside days remaining).
export const CONTRACT_TYPES_WITH_MONEY_POT_BALANCE = [ACCOUNTS_CONTRACTS_TYPES.Casual_Accruing];

// Contracts that accrue holiday as workdays worked (1 day off per N days worked).
// Used to gate days-based balance display.
export const CONTRACT_TYPES_WITH_WORKDAYS_ACCRUAL = [
  ACCOUNTS_CONTRACTS_TYPES.MINI_DE,
  ACCOUNTS_CONTRACTS_TYPES.MIDI_DE,
  ACCOUNTS_CONTRACTS_TYPES.MIDI_MONTHLY_DE
];

// Contracts that accrue overtime hours into a Time-Off-In-Lieu (TOIL) pot.
// Used to gate hoursPerWorkday + employmentRate inputs and TOIL balance display.
export const CONTRACT_TYPES_WITH_TOIL_ACCRUAL = [ACCOUNTS_CONTRACTS_TYPES.PERM_DE];

export const PTO_TYPES = {
  Holiday: 'Holiday',
  SickDay: 'SickDay',
  TimeOffInLieu: 'TimeOffInLieu'
};

export const contractTypeToTranslationKey = {
  [ACCOUNTS_CONTRACTS_TYPES.Casual]: 'contractTypes_casual',
  [ACCOUNTS_CONTRACTS_TYPES.MGH]: 'contractTypes_MGH',
  [ACCOUNTS_CONTRACTS_TYPES.Permanent]: 'contractTypes_permanent',
  [ACCOUNTS_CONTRACTS_TYPES.Casual_Accruing]: 'contractTypes_casual_accruing',
  [ACCOUNTS_CONTRACTS_TYPES.MINI_DE]: 'contractTypes_mini_de',
  [ACCOUNTS_CONTRACTS_TYPES.MIDI_DE]: 'contractTypes_midi_de',
  [ACCOUNTS_CONTRACTS_TYPES.MIDI_MONTHLY_DE]: 'contractTypes_midi_monthly_de',
  [ACCOUNTS_CONTRACTS_TYPES.PERM_DE]: 'contractTypes_perm_de',
  [ACCOUNTS_CONTRACTS_TYPES.BLUE_COLLAR_SWEDEN]: 'contractTypes_blue_collar_se',
  [ACCOUNTS_CONTRACTS_TYPES.BLUE_COLLAR_PERM_SWEDEN]: 'contractTypes_blue_collar_perm_se',
  [ACCOUNTS_CONTRACTS_TYPES.WHITE_COLLAR_SWEDEN]: 'contractTypes_white_collar_se',
  [ACCOUNTS_CONTRACTS_TYPES.NON_PAID_SWEDEN]: 'contractTypes_non_paid_se'
};

export const PAYROLL_VISIBILITY_MODES = {
  FULL: 'full',
  SHARED_FIELDS_ONLY: 'shared_fields_only'
};
